import { StateModel, StateModuleInfo } from '../models/state.model';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getMenus, getModules } from '../../api/resource';
import { moduleMenuItemToTreeNode } from '../../utils/moduleResponseToTreeNode';
import { SidebarTreeNode } from '../../models/SidebarTreeNode';
import { ModuleInfoResponse } from '../../models/ModuleInfoResponse';

const initialState: StateModuleInfo = {
  loadedModules: [],
  modulesTreeData: [],
  isModulesLoading: false,
  needsReload: false,
  refreshVersion: 0,
  moduleHashes: {},
};

export const fetchModules = createAsyncThunk(
  'base/modules/modules',
  async () => {
    const res = await getModules();
    return res.data;
  },
  {
    condition: (args, { getState }) => {
      const { modules } = getState() as StateModel;
      return !modules.isModulesLoading;
    },
  },
);

type FetchModuleChildrenArgs = {
  moduleName: string;
  parentId: number;
  parentNode: SidebarTreeNode;
};

export const fetchModuleChildren = createAsyncThunk(
  'modules/menuItems',
  async ({ moduleName, parentId }: FetchModuleChildrenArgs, { rejectWithValue }) => {
    try {
      const res = await getMenus(moduleName, parentId);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

function mergeChildrenToNode(
  nodes: SidebarTreeNode[],
  targetKey: string,
  newChildren: SidebarTreeNode[],
): SidebarTreeNode[] {
  return nodes.map((node) => {
    if (node.key === targetKey) {
      const oldChildrenMap = new Map((node.children || []).map((child) => [child.key, child]));

      const merged = newChildren.map((newChild) => {
        const oldChild = oldChildrenMap.get(newChild.key);
        if (oldChild) {
          return {
            ...oldChild,
            title: newChild.title,
            isLeaf: newChild.isLeaf,
            sidebarNodeActions: newChild.sidebarNodeActions,
            id: newChild.id,
            parentNode: newChild.parentNode,
            moduleKey: newChild.moduleKey,
          };
        }
        return newChild;
      });

      return {
        ...node,
        children: merged,
      };
    }

    if (node.children) {
      return {
        ...node,
        children: mergeChildrenToNode(node.children, targetKey, newChildren),
      };
    }

    return node;
  });
}

function addChildrenToNode(
  nodes: Array<SidebarTreeNode>,
  targetKey: string,
  newChildren: Array<SidebarTreeNode>,
): Array<SidebarTreeNode> {
  return nodes.map((node) => {
    if (node.key === targetKey) {
      return {
        ...node,
        children: newChildren,
      };
    }

    if (node.children) {
      return {
        ...node,
        children: addChildrenToNode(node.children, targetKey, newChildren),
      };
    }

    return node;
  });
}

const modulesSlice = createSlice({
  name: 'modules',
  initialState,
  reducers: {
    invalidateModules(state) {
      state.needsReload = true;
    },
    resetModuleHashes(state) {
      state.moduleHashes = {};
    },
    setModuleHash(state, action: PayloadAction<{ moduleName: string; hash: string }>) {
      state.moduleHashes[action.payload.moduleName] = action.payload.hash;
    },
    updateNodeChildren(
      state,
      action: PayloadAction<{
        nodeKey: string;
        children: SidebarTreeNode[];
      }>,
    ) {
      const { nodeKey, children: newChildren } = action.payload;
      state.modulesTreeData = mergeChildrenToNode(state.modulesTreeData, nodeKey, newChildren);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchModules.pending, (state: StateModuleInfo) => {
      state.isModulesLoading = true;
    });
    builder.addCase(fetchModules.fulfilled, (state: StateModuleInfo, action) => {
      const modules: ModuleInfoResponse[] = action.payload;

      const newRootNodes: SidebarTreeNode[] = modules.map((m) => ({
        title: m.title,
        key: m.name,
        selectable: false,
        children: [],
        isLeaf: false,
        parentNode: null,
        moduleKey: m.name,
        id: null,
        sidebarNodeAction: null,
        iconUrl: m.iconUrl,
      }));

      const oldRootsMap = new Map(state.modulesTreeData.map((n) => [n.key, n]));

      const mergedTreeData = newRootNodes.map((newNode) => {
        const oldNode = oldRootsMap.get(newNode.key);
        if (oldNode) {
          return {
            ...oldNode,
            title: newNode.title,
            iconUrl: newNode.iconUrl,
            moduleKey: newNode.moduleKey,
          };
        }
        return newNode;
      });

      state.loadedModules = modules;
      state.modulesTreeData = mergedTreeData;
      state.isModulesLoading = false;
      state.needsReload = false;
      state.refreshVersion += 1;

      const hashes: Record<string, string> = {};

      modules.forEach((module) => {
        if (module.hash) {
          const moduleKey = module.classifier || module.name;
          hashes[moduleKey] = module.hash;
        }
      });

      state.moduleHashes = hashes;
    });
    builder.addCase(fetchModules.rejected, (state: StateModuleInfo) => {
      state.loadedModules = [];
      state.modulesTreeData = [];
      state.isModulesLoading = false;
    });
    builder.addCase(fetchModuleChildren.fulfilled, (state, action) => {
      const { parentNode } = action.meta.arg;
      const newChildren = moduleMenuItemToTreeNode(parentNode!, action.payload);
      state.modulesTreeData = addChildrenToNode(state.modulesTreeData, parentNode.key, newChildren);
    });
  },
});

export const { invalidateModules, resetModuleHashes, setModuleHash, updateNodeChildren } =
  modulesSlice.actions;

const ModulesReducer = modulesSlice.reducer;

export default ModulesReducer;
